<template>
  <div
    :class="[
      'player-profile-image',
      {
        missing: !isSkeleton && (!imageBlob || !expand),
        loading: isImageLoading,
        loaded: isImageLoaded,
        'is-cta': isMyProfile && !imageBlob && !isSkeleton,
      },
    ]"
  >
    <div v-if="isSkeleton || isImageLoading || isUploading" class="skeleton-item skeleton o-2" />

    <div class="image-container" @click="expandImage">
      <div v-if="imageBlob" class="image">
        <img loading="lazy" :src="getImageSrc()" alt="image" @load="imageLoadEnd" @error="imageLoadError" />
      </div>
      <div v-else-if="!isSkeleton" class="missing-icon">
        <gam-icon v-bind="getMissingIcon" />
        <span v-if="isMyProfile" class="add-photo-label">Add photo</span>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { gambits } from '@/core/gambits';
import FormHelper from '@/core/helpers/form.helper';
import { AppEndpoint } from '@/core/network/api/constants/api.url.constant';
import GamIcon from '@/views/components/GamIcon.vue';
import { GamIconName } from '@/views/composables/constants/components/gamIcon.constants';
import { GamPlayerImageSize } from '@/views/composables/constants/components/gamPlayer.constants';
import { GamComponentsEmits } from '@/views/composables/constants/main/emit.constants';
import type { GamIconType } from '@/views/composables/models/components/GamIcon';
import type { GamPlayerImageType } from '@/views/composables/models/components/GamPlayer';
import { useFileDialog } from '@vueuse/core';
import { computed, ref, watch } from 'vue';

export interface PlayerProfileImageProps extends GamPlayerImageType {
  isSkeleton?: boolean;
  isMyProfile?: boolean;
  isUploading?: boolean;
}

const props = withDefaults(defineProps<PlayerProfileImageProps>(), {
  size: GamPlayerImageSize.LARGE,
  expand: true,
  isMyProfile: false,
  isUploading: false,
});

const emit = defineEmits([GamComponentsEmits.EXPAND, 'upload']);

const { open, onChange } = useFileDialog({ accept: '.jpg, .jpeg, .png', multiple: false });

onChange(async (files) => {
  if (files?.length) {
    const base64 = await FormHelper.readFileAsBase64(files[0]);
    emit('upload', `data:image/png;base64,${base64}`);
  }
});

const getImageSrc = (): string => {
  let url = `${gambits.configService.getServiceRoutingHost()}${AppEndpoint.BLOB}`;
  url = url.replace(new RegExp('{blobHash}', 'g'), props.imageBlob || '');
  return `${url}?width=256`;
};

const isImageLoading = ref<boolean>(false);
const isImageLoaded = ref<boolean>(false);
const isImageError = ref<boolean>(false);

watch(
  () => props.imageBlob,
  (newImage, prevImage) => {
    if (!newImage || newImage === prevImage) {
      return;
    }

    isImageLoading.value = true;
    isImageLoaded.value = false;
  },
);

const imageLoadEnd = () => {
  setTimeout(() => {
    isImageLoading.value = false;
    isImageLoaded.value = true;
  });
};
const imageLoadError = () => {
  isImageLoading.value = false;
  isImageError.value = true;
};
const expandImage = () => {
  if (props.isMyProfile) {
    open();
    return;
  }
  if (!props.imageBlob) return;

  emit(GamComponentsEmits.EXPAND, props.imageBlob);
};

const getMissingIcon = computed((): GamIconType => {
  return {
    name: GamIconName.PAWN_EMPTY,
    size: '36px',
  };
});
</script>

<style lang="scss" scoped>
@use '@/ui/css/mixins' as *;
@use '@/ui/css/partial';

.player-profile-image {
  display: flex;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
  transition: border var(--transition);
  overflow: hidden;
  background: var(--color-dark-500);

  @extend %gam-special-border;

  width: 100%;
  height: 100%;

  &.loading {
    background-color: var(--color-dark-500);
    transition: none;
  }

  &.loaded {
    background-color: var(--color-dark-500);
    transition: background-color var(--transition-router);
  }

  &.missing {
    background: var(--color-dark-700);
    transition: background-color var(--transition-router);
  }

  &:before {
    background: var(--color-white-100);
    padding: 3px;
    opacity: 0;
    z-index: 100;
  }

  .image-container {
    width: inherit;
    height: inherit;
    display: flex;
    align-items: center;
    justify-content: center;

    .image {
      width: inherit;
      height: inherit;

      &.skeleton {
        border-radius: 0 !important;
      }

      img {
        width: 100%;
        height: auto;
        border-radius: 100%;
        aspect-ratio: 1 / 1;
        object-fit: cover;
        filter: grayscale(100%);
        -webkit-user-drag: none;
        -webkit-user-select: none;
        user-select: none;
      }
    }
  }

  &.is-cta {
    cursor: pointer;

    @include press;
  }

  .missing-icon {
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  .add-photo-label {
    font-size: 12px;
    color: var(--color-white-70);
    margin-top: var(--spacing-s);
  }
  @include hover-capable {
    &:not(.missing):hover {
      cursor: pointer;
    }
  }

  .expand-icon {
    position: absolute;
    right: 6px;
    bottom: 6px;
  }
}

img {
  opacity: 0;
}

.loaded img {
  opacity: 1;
  transition: opacity var(--transition-router);
}

.skeleton {
  position: absolute;
  width: 100%;
  height: 100%;
}
</style>
